import React, { useCallback, useEffect, useState } from 'react';
import DateRangeSelector from '../DateRangeSelector';
import useLatestRequest from '../../Hooks/useLatestRequest';
import { useAgentApi } from '../../Services/AgentApiProvider';
import { AgentMetricsResponse } from '../../Services/AgentApiProviderTypes';
import { ExecutionOutcome, formatCount, METRIC_CHARTS, OUTCOMES, total } from './agentMetricCharts';
import { LineChart } from './LineChart';
import './AgentMetrics.css';

interface Period {
  start: string;
  end: string | null;
}

const OUTCOME_ICONS: Record<ExecutionOutcome, string> = Object.fromEntries(
  OUTCOMES.map(({ outcome, icon }) => [outcome, icon]),
) as Record<ExecutionOutcome, string>;

const StatusIcon = ({ outcome }: { outcome: ExecutionOutcome }) => (
  <i
    className={`fa-solid ${OUTCOME_ICONS[outcome]} agent-metrics-status-${outcome}`}
    aria-hidden="true"
  />
);

interface AgentMetricsTabProps {
  agentId: string;
}

/**
 * The metrics of one agent. Each chart has a single line, so it needs no
 * legend; how executions ended wears the status colors, the averages the
 * first series color.
 */
export default function AgentMetricsTab({ agentId }: AgentMetricsTabProps) {
  const { getAgentMetrics } = useAgentApi();
  const beginRequest = useLatestRequest();

  const [period, setPeriod] = useState<Period | null>(null);
  const [metrics, setMetrics] = useState<AgentMetricsResponse | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    if (!period) return;

    const isLatest = beginRequest();
    setStatus('loading');

    getAgentMetrics(period.start, period.end, [agentId])
      .then((response) => {
        if (!isLatest()) return;
        setMetrics(response);
        setStatus('ready');
      })
      .catch(() => {
        if (isLatest()) setStatus('failed');
      });
  }, [period, agentId, getAgentMetrics, beginRequest]);

  const handlePeriodChange = useCallback(
    (range: { start_date: string; end_date: string | null }) => {
      setPeriod({ start: range.start_date, end: range.end_date });
    },
    [],
  );

  const renderBody = () => {
    if (status === 'failed') {
      return <p className="agent-metrics-message">Agent metrics could not be loaded.</p>;
    }
    if (!metrics) {
      return <p className="agent-metrics-message">Loading agent metrics…</p>;
    }
    if (metrics.series === null) {
      return <p className="agent-metrics-message">Execution metrics are unavailable.</p>;
    }

    const agent = metrics.agents.find((item) => item.id === agentId);
    if (!agent) {
      return <p className="agent-metrics-message">This agent has no metrics.</p>;
    }

    const series = metrics.series;

    return (
      <>
        <ul className="agent-metrics-summary" aria-label="Executions in this period">
          {OUTCOMES.map(({ metric, outcome }) => (
            <li key={outcome}>
              <StatusIcon outcome={outcome} />
              <strong>{formatCount(total(series[metric]?.[agent.id]))}</strong>
              <span>{outcome}</span>
            </li>
          ))}
        </ul>

        <div className={`agent-metrics-grid${status === 'loading' ? ' is-refreshing' : ''}`}>
          {METRIC_CHARTS.map((chart) => (
            <LineChart
              key={chart.metric}
              title={chart.title}
              icon={chart.outcome ? <StatusIcon outcome={chart.outcome} /> : undefined}
              buckets={metrics.buckets}
              stepSeconds={metrics.step_seconds}
              series={[
                {
                  id: agent.id,
                  name: agent.name,
                  color: chart.outcome ?? 'series-1',
                  values: series[chart.metric]?.[agent.id] ?? [],
                },
              ]}
              formatValue={chart.formatValue}
              unit={chart.unit}
              integerTicks={chart.integerTicks}
              emptyMessage="No data in this period"
            />
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="agent-metrics">
      <div className="agent-metrics-filters">
        <DateRangeSelector storageKey="agent-metrics" onChange={handlePeriodChange} />
      </div>
      {renderBody()}
    </div>
  );
}
